import { useRef, useCallback, useState } from 'react';

export interface UseRoomRecorderResult {
  isRecording: boolean;
  startRecording: () => void;
  stopRecording: () => Promise<Blob | null>;
  /** Stop and throw away everything captured so far (see ticket 30). */
  discardRecording: () => void;
  addStream: (stream: MediaStream) => void;
  removeStream: (stream: MediaStream) => void;
}

/**
 * Hook that records mixed audio from multiple MediaStreams
 * using AudioContext + MediaRecorder.
 *
 * The host calls startRecording() when the room begins,
 * addStream() for each peer's remote audio,
 * and stopRecording() when the room ends to get the final Blob.
 */
export function useRoomRecorder(): UseRoomRecorderResult {
  const [isRecording, setIsRecording] = useState(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const destRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const sourcesRef = useRef<Map<MediaStream, MediaStreamAudioSourceNode>>(
    new Map(),
  );

  const startRecording = useCallback(() => {
    const ctx = new AudioContext();
    const dest = ctx.createMediaStreamDestination();

    audioCtxRef.current = ctx;
    destRef.current = dest;
    chunksRef.current = [];

    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/webm';

    const recorder = new MediaRecorder(dest.stream, { mimeType });

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    recorderRef.current = recorder;
    recorder.start(1000); // collect chunks every second
    setIsRecording(true);
  }, []);

  const addStream = useCallback((stream: MediaStream) => {
    const ctx = audioCtxRef.current;
    const dest = destRef.current;
    if (!ctx || !dest) return;
    if (sourcesRef.current.has(stream)) return;

    const source = ctx.createMediaStreamSource(stream);
    source.connect(dest);
    sourcesRef.current.set(stream, source);
  }, []);

  const removeStream = useCallback((stream: MediaStream) => {
    const source = sourcesRef.current.get(stream);
    if (source) {
      source.disconnect();
      sourcesRef.current.delete(stream);
    }
  }, []);

  const teardown = useCallback(() => {
    for (const source of sourcesRef.current.values()) {
      source.disconnect();
    }
    sourcesRef.current.clear();
    void audioCtxRef.current?.close();
    audioCtxRef.current = null;
    destRef.current = null;
    recorderRef.current = null;
    setIsRecording(false);
  }, []);

  const stopRecording = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        resolve(
          chunksRef.current.length > 0
            ? new Blob(chunksRef.current, { type: 'audio/webm' })
            : null,
        );
        setIsRecording(false);
        return;
      }

      recorder.onstop = () => {
        const blob =
          chunksRef.current.length > 0
            ? new Blob(chunksRef.current, { type: 'audio/webm' })
            : null;

        teardown();

        resolve(blob);
      };

      recorder.stop();
    });
  }, [teardown]);

  // Stop and throw the audio away. Used when a room turns out to leave no
  // trace — private rooms and rooms in private communities — where keeping
  // what was captured would defeat the point (ticket 30). Detaching
  // ondataavailable first matters: stop() flushes one final chunk, which
  // would otherwise land in a buffer we have just emptied.
  const discardRecording = useCallback(() => {
    const recorder = recorderRef.current;
    chunksRef.current = [];

    if (recorder && recorder.state !== 'inactive') {
      recorder.ondataavailable = null;
      recorder.onstop = () => {
        chunksRef.current = [];
        teardown();
      };
      recorder.stop();
    } else {
      teardown();
    }
  }, [teardown]);

  return {
    isRecording,
    startRecording,
    stopRecording,
    discardRecording,
    addStream,
    removeStream,
  };
}
